// src/services/proveedores.service.js - MIGRADO A FIREBASE
import FirebaseService from './firebase.service';
import ApiService from './api.service';

// Datos de respaldo para proveedores
const PROVEEDORES_RESPALDO = [
  {
    id: '1',
    nombre: 'Proveedor General',
    contacto: 'Sin contacto',
    telefono: '',
    email: '',
    direccion: '',
    activo: true,
    fechaCreacion: new Date().toISOString()
  }
];

/**
 * Servicio para gestión de proveedores con Firebase
 * Migrado completamente a Firebase Functions
 */
class ProveedoresService extends FirebaseService {
  constructor() {
    super('/proveedores'); // Módulo en Firebase Functions
  }

  /**
   * Obtiene todos los proveedores
   * @returns {Promise<Array>} Lista de proveedores
   */
  async obtenerTodos() {
    console.log('🔄 Obteniendo todos los proveedores...');
    const proveedores = await this.get('');
    const proveedoresArray = this.ensureArray(proveedores);
    console.log(`✅ Proveedores cargados: ${proveedoresArray.length}`);
    return proveedoresArray;
  }

  /**
   * Obtiene proveedores activos
   * @returns {Promise<Array>} Lista de proveedores activos
   */
  async obtenerActivos() {
    try {
      console.log('🔄 Obteniendo proveedores activos...');
      const proveedores = await this.get('/activos');
      
      const proveedoresArray = this.ensureArray(proveedores);
      
      if (proveedoresArray.length === 0) {
        const activos = PROVEEDORES_RESPALDO.filter(p => p.activo);
        console.log('⚠️ Usando proveedores activos de respaldo');
        return activos;
      }
      
      console.log(`✅ Proveedores activos: ${proveedoresArray.length}`);
      return proveedoresArray;
      
    } catch (error) {
      console.error('❌ Error al obtener proveedores activos:', error);
      return PROVEEDORES_RESPALDO.filter(p => p.activo);
    }
  }

  /**
   * Obtiene un proveedor por su ID
   * @param {string} id - ID del proveedor
   * @returns {Promise<Object>} Proveedor
   */
  /** POS-SUPPLIERS-FIN-003 */
  async obtenerCuentaCorriente(id) {
    const res = await this.get(`/${id}/cuenta-corriente`);
    return res?.data || res;
  }

  /** POS-SUPPLIERS-CXP-DASHBOARD-001C — resumen consolidado CxP (path raíz API) */
  async obtenerResumenCuentasPorPagar() {
    const api = new ApiService('');
    const response = await api.get('/cuentas-por-pagar/resumen');
    const { data, status } = response || {};
    if (typeof status === 'number' && status >= 400) {
      const message = (data && (data.message || data.error)) || 'Error al cargar cuentas por pagar';
      const error = new Error(message);
      error.response = { status, data };
      throw error;
    }
    const payload = data?.data !== undefined ? data.data : data;
    return payload;
  }

  async registrarFactura(id, payload) {
    return this.post(`/${id}/facturas`, payload);
  }

  async registrarPagoCxp(id, payload) {
    return this.post(`/${id}/pagos`, payload);
  }

  async registrarNotaCredito(id, payload) {
    return this.post(`/${id}/notas-credito`, payload);
  }

  async obtenerPorId(id) {
    try {
      console.log(`🔄 Obteniendo proveedor ID: ${id}`);
      const proveedor = await this.get(`/${id}`);
      
      const proveedorObj = this.ensureObject(proveedor);
      
      if (!proveedorObj || Object.keys(proveedorObj).length === 0) {
        // Buscar en datos de respaldo
        const proveedorRespaldo = PROVEEDORES_RESPALDO.find(p => p.id === id);
        if (proveedorRespaldo) {
          console.log('⚠️ Usando proveedor de respaldo');
          return proveedorRespaldo;
        }
        throw new Error(`Proveedor ${id} no encontrado`);
      }
      
      console.log(`✅ Proveedor obtenido:`, proveedorObj);
      return proveedorObj;
      
    } catch (error) {
      console.error(`❌ Error al obtener proveedor ${id}:`, error);
      
      // Buscar en datos de respaldo antes de lanzar error
      const proveedorRespaldo = PROVEEDORES_RESPALDO.find(p => p.id === id);
      if (proveedorRespaldo) {
        console.log('⚠️ Usando proveedor de respaldo');
        return proveedorRespaldo;
      }
      
      throw error;
    }
  }

  /**
   * Crea un nuevo proveedor
   * @param {Object} proveedor - Datos del proveedor
   * @returns {Promise<Object>} Proveedor creado
   */
  async crear(proveedor) {
    try {
      console.log('🆕 Creando proveedor:', proveedor);
      
      // Formatear datos del proveedor
      const proveedorFormateado = {
        ...proveedor,
        nombre: proveedor.nombre?.trim() || '',
        contacto: proveedor.contacto?.trim() || '',
        telefono: proveedor.telefono?.trim() || '',
        email: proveedor.email?.trim() || '',
        direccion: proveedor.direccion?.trim() || '',
        activo: proveedor.activo !== false // Por defecto true
      };
      
      // Validación básica
      if (!proveedorFormateado.nombre) {
        throw new Error('El nombre del proveedor es requerido');
      }
      
      const resultado = await this.post('', proveedorFormateado);
      console.log('✅ Proveedor creado:', resultado);
      
      return resultado;
    } catch (error) {
      console.error('❌ Error al crear proveedor:', error);
      throw error;
    }
  }

  /**
   * Actualiza un proveedor existente
   * @param {string} id - ID del proveedor
   * @param {Object} proveedor - Nuevos datos
   * @returns {Promise<Object>} Respuesta de la actualización
   */
  async actualizar(id, proveedor) {
    try {
      console.log(`🔄 Actualizando proveedor ${id}:`, proveedor);
      
      // Formatear datos del proveedor
      const proveedorFormateado = {
        ...proveedor,
        nombre: proveedor.nombre?.trim() || '',
        contacto: proveedor.contacto?.trim() || '',
        telefono: proveedor.telefono?.trim() || '',
        email: proveedor.email?.trim() || '',
        direccion: proveedor.direccion?.trim() || ''
      };
      
      // Validación básica
      if (!proveedorFormateado.nombre) {
        throw new Error('El nombre del proveedor es requerido');
      }
      
      const resultado = await this.put(`/${id}`, proveedorFormateado);
      console.log('✅ Proveedor actualizado:', resultado);
      
      return resultado;
    } catch (error) {
      console.error(`❌ Error al actualizar proveedor ${id}:`, error);
      throw error;
    }
  }

  /**
   * Elimina un proveedor
   * @param {string} id - ID del proveedor
   * @returns {Promise<Object>} Respuesta de la eliminación
   */
  async eliminar(id) {
    try {
      console.log(`🗑️ Eliminando proveedor ${id}`);
      const resultado = await this.delete(`/${id}`);
      console.log('✅ Proveedor eliminado:', resultado);
      
      return resultado;
    } catch (error) {
      console.error(`❌ Error al eliminar proveedor ${id}:`, error);
      throw error;
    }
  }

  /**
   * Busca proveedores por nombre
   * @param {string} termino - Término de búsqueda
   * @returns {Promise<Array>} Proveedores encontrados
   */
  async buscar(termino) {
    const q = String(termino || '').trim();
    if (!q) {
      return this.obtenerTodos();
    }

    const matchLocal = (list, term) => {
      const t = term.toLowerCase();
      return (list || []).filter((p) => {
        const haystack = [
          p.nombre,
          p.contacto,
          p.email,
          p.telefono,
          p.cuit,
          p.cuil,
          p.documento,
          p.razon_social,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        return haystack.includes(t);
      });
    };

    try {
      const proveedores = await this.get('/buscar', { termino: q });
      return this.ensureArray(proveedores);
    } catch (error) {
      // Fallback: listado real + filtro local (nunca datos fake de respaldo)
      console.warn('⚠️ [PROVEEDORES] /buscar falló, filtro local:', error?.message || error);
      const all = await this.obtenerTodos();
      const real = (all || []).filter((p) => p && p.id !== '1');
      return matchLocal(real, q);
    }
  }

  /**
   * Obtiene estadísticas de un proveedor
   * @param {string} id - ID del proveedor
   * @returns {Promise<Object>} Estadísticas del proveedor
   */
  async obtenerEstadisticas(id) {
    try {
      console.log(`🔄 Obteniendo estadísticas del proveedor ${id}`);
      const estadisticas = await this.get(`/${id}/estadisticas`);
      
      const statsObj = this.ensureObject(estadisticas);
      console.log(`📊 Estadísticas del proveedor:`, statsObj);
      
      return statsObj;
    } catch (error) {
      console.error(`❌ Error al obtener estadísticas del proveedor ${id}:`, error);
      return {
        totalCompras: 0,
        montoTotal: 0,
        ultimaCompra: null,
        comprasRecientes: 0
      };
    }
  }
}

export default new ProveedoresService();